import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import SEO from '../components/common/SEO';
import { profileService, UpdateProfileData } from '../services/services/profileService';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../contexts/ConfirmationContext';
import './EditProfilePage.scss';

const EditProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user, checkAuthStatus } = useAuth();
  const { alert } = useConfirmation();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<UpdateProfileData>({
    first_name: '',
    last_name: '',
    bio: '',
    phone_number: '',
    date_of_birth: '',
    address: '',
  });
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  useEffect(() => {
    if (user && username === user.username) {
      loadProfile();
    } else if (user && username !== user.username) {
      navigate(`/profile/${user.username}/edit`);
    }
  }, [user, username]);

  const loadProfile = async () => {
    if (!username) return;
    
    setLoading(true);
    try {
      const data = await profileService.getProfile(username);
      if (data.profile) {
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          bio: data.profile.bio || '',
          phone_number: data.profile.phone_number || '',
          date_of_birth: data.profile.date_of_birth || '',
          address: data.profile.address || '',
        });
        setAvatarPreview(data.profile.avatar_url);
      }
    } catch (error: any) {
      alert({
        message: error.response?.data?.error || 'Failed to load profile',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username) return;
    
    setSaving(true);
    try {
      const updateData: UpdateProfileData = { ...formData };
      
      if (avatarFile) {
        updateData.profile_picture = avatarFile;
      }
      
      await profileService.updateProfile(updateData);
      
      // Refresh user data
      await checkAuthStatus();
      
      alert({
        message: 'Profile updated successfully!',
        variant: 'success',
      });
      
      navigate(`/profile/${username}`);
    } catch (error: any) {
      alert({
        message: error.response?.data?.error || 'Failed to update profile',
        variant: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container my-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading profile...</p>
      </div>
    );
  }

  if (!user || username !== user.username) {
    return (
      <div className="container my-5">
        <Alert variant="danger">You don't have permission to edit this profile</Alert>
      </div>
    );
  }

  return (
    <div className="edit-profile-page">
      <SEO 
        title={`Edit Profile | Quicket`}
        description="Edit your profile information on Quicket."
      />
      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-8">
            {/* Header Section */}
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <Link to={`/profile/${username}`} className="text-muted text-decoration-none mb-2 d-inline-flex align-items-center">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Profile
                  </Link>
                  <h2 className="mb-0 mt-2">
                    <i className="fas fa-user-edit me-2 text-primary"></i>
                    Edit Profile
                  </h2>
                </div>
              </div>
            </div>

            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4 p-md-5">
                <Form onSubmit={handleSubmit}>
                  {/* Avatar Section */}
                  <div className="text-center mb-5">
                    <div className="position-relative d-inline-block mb-3">
                      <div className="avatar-wrapper">
                        <img
                          src={avatarPreview || `https://ui-avatars.com/api/?name=${encodeURIComponent((formData.first_name + ' ' + formData.last_name).trim() || 'User')}&background=6366f1&color=fff&size=200&bold=true`}
                          className="profile-avatar-edit rounded-circle border border-4 border-primary shadow-lg"
                          alt="Profile Avatar"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            const name = (formData.first_name + ' ' + formData.last_name).trim() || 'User';
                            target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=200&bold=true`;
                          }}
                        />
                        <label className="edit-avatar-overlay" htmlFor="avatar-input" title="Change profile picture">
                          <i className="fas fa-camera"></i>
                        </label>
                        <input
                          type="file"
                          id="avatar-input"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="d-none"
                        />
                      </div>
                    </div>
                    <p className="text-muted mb-0">
                      <i className="fas fa-info-circle me-1"></i>
                      Click the camera icon to change your profile picture
                    </p>
                  </div>

                  <hr className="my-4" />

                  {/* Name Fields */}
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          <i className="fas fa-user me-2 text-primary"></i>
                          First Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          required
                          className="form-control-lg"
                          placeholder="Enter your first name"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          <i className="fas fa-user me-2 text-primary"></i>
                          Last Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          required
                          className="form-control-lg"
                          placeholder="Enter your last name"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  {/* Phone Number */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-phone me-2 text-primary"></i>
                      Phone Number
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      className="form-control-lg"
                      placeholder="+1 (555) 123-4567"
                    />
                    <Form.Text className="text-muted">
                      Optional - Your phone number for contact purposes
                    </Form.Text>
                  </Form.Group>

                  {/* Date of Birth */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-calendar me-2 text-primary"></i>
                      Date of Birth
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleInputChange}
                      className="form-control-lg"
                    />
                    <Form.Text className="text-muted">
                      Optional - Your date of birth
                    </Form.Text>
                  </Form.Group>

                  {/* Address */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                      Address
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="form-control-lg"
                      placeholder="123 Main Street, City, State, ZIP"
                    />
                    <Form.Text className="text-muted">
                      Optional - Your physical address
                    </Form.Text>
                  </Form.Group>

                  {/* Bio */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-pen me-2 text-primary"></i>
                      Bio
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      className="form-control-lg"
                      placeholder="Tell us about yourself... Share your interests, hobbies, or anything you'd like others to know about you."
                      style={{ resize: 'vertical' }}
                    />
                    <Form.Text className="text-muted">
                      Optional - A brief description about yourself (max 500 characters)
                    </Form.Text>
                  </Form.Group>

                  <hr className="my-4" />

                  {/* Submit Buttons */}
                  <div className="d-flex gap-3 justify-content-end flex-wrap">
                    <Link 
                      to={`/profile/${username}`} 
                      className="btn btn-outline-secondary btn-lg px-4"
                    >
                      <i className="fas fa-times me-2"></i>
                      Cancel
                    </Link>
                    <Button 
                      type="submit" 
                      variant="primary" 
                      disabled={saving}
                      className="btn-lg px-4"
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving Changes...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfilePage;

